<template>
  <div
      class="base-calculator__branch-icon"
      :class="{
        selected,
        deselected,
      }"
      :style="cssVariables"
  >
    <div>
      <img :src="branch.image?.url" :alt="`Icon ${branch.name}`">
    </div>
    <div>
      <div
          ref="branchLabel"
          class="base-calculator__branch-icon__label"
          v-html="branch.name"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import BranchInterface from '@/interfaces/BranchInterface';
import {
  computed, nextTick, onBeforeUnmount, onMounted, onUpdated, ref,
} from 'vue';

const props = defineProps<{
  branch: BranchInterface
  selected?: boolean
  deselected?: boolean
}>();

const branchLabel = ref<InstanceType<typeof HTMLElement>>();
const labelVariables = ref<Record<string, string>>({});
const cssVariables = computed(() => ({
  ...labelVariables.value,
  '--branch-color': props.branch.color,
}));

const isVisible = () => !!(branchLabel.value?.offsetWidth || branchLabel.value?.offsetHeight || branchLabel.value?.getClientRects().length);
const setCssVariables = async () => {
  if (branchLabel.value === undefined) return;
  if (!('style' in (branchLabel.value || {}))) return;

  if (!isVisible()) {
    await nextTick(setCssVariables);
    return;
  }

  branchLabel.value.style.setProperty('transition', 'none');
  branchLabel.value.style.setProperty('width', 'auto');
  branchLabel.value.classList.add('check-size');

  labelVariables.value['--label-width'] = `${branchLabel.value.offsetWidth}px`;

  branchLabel.value.classList.remove('check-size');
  branchLabel.value.style.removeProperty('width');
  branchLabel.value.style.removeProperty('transition');
};

onUpdated(() => {
  if (isVisible()) return;

  setCssVariables();
});

onMounted(() => {
  window.addEventListener('resize', setCssVariables);
  setCssVariables();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setCssVariables);
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.base-calculator__branch-icon {
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
  border: 2px solid #fff;
  border-radius: 5rem;
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: auto auto;

  &, > * {
    cursor: pointer;
    user-select: none;
  }

  > div {
    &:first-child {
      position: relative;
      padding: 10px;
      height: calc(3.75rem + 20px);
      width: calc(3.75rem + 20px);

      > img {
        height: 3.75rem;
        width: auto;
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
        pointer-events: none;
        opacity: 1;
        transition: opacity .425s ease-in-out;
      }
    }

    &:nth-child(2) {
      align-self: center;
      overflow: hidden;

      > div {
        box-sizing: border-box;
        color: $body-color;
        @include font-regular(24px);
        font-weight: 300;
        line-height: 1;
        opacity: 0;
        width: 0;
        overflow: hidden;
        --padding-left: 0.625em;
        --padding-right: 1.04166em;
        padding-left: 0;
        padding-right: 0;

        &.check-size {
          padding-left: var(--padding-left);
          padding-right: var(--padding-right);
        }

        transition: opacity .425s ease-in-out, width .425s ease-in-out, padding-left .425s ease-in-out, padding-right .425s ease-in-out;
      }
    }
  }

  &:hover, &.selected {
    > div {
      &:nth-child(2) {
        align-self: center;

        > div {
          opacity: 1;
          width: var(--label-width);
          padding-left: var(--padding-left);
          padding-right: var(--padding-right);
        }
      }
    }
  }

  &.selected {
    border: 2px solid var(--branch-color, #{$primary});
  }

  &.deselected {
    > div:first-child > img {
      opacity: 0.5;
    }
  }
}
</style>
